import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Input,
  Select,
  Skeleton,
  Table,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  type OnChangeFn,
} from '@servicecompressionllc/sc-design-system';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
} from '@servicecompressionllc/sc-design-system/icons';
import { AppShell } from '../../components/AppShell.js';
import { Pagination } from '../../components/Pagination.js';
import { ScopeSummary } from '../../components/ScopeSummary.js';
import { useMe } from '../../hooks/useMe.js';
import type { User, UserStatus } from '../../lib/types.js';
import { useGroupsListLogic } from '../groups/useGroupsLogic.js';
import { useUsersListLogic } from './useUsersListLogic.js';
import { useUsersBulkMembershipLogic } from './useUsersBulkMembershipLogic.js';

const PAGE_SIZE = 25;

const SORTABLE = ['email', 'name', 'status', 'lastLoginAt'];
const DEFAULT_SORT = 'lastLoginAt.desc';

/** `?sort=<column>.<asc|desc>`; anything unrecognised falls back to the default. */
const parseSort = (raw: string | null): SortingState => {
  const [id = '', dir] = (raw ?? DEFAULT_SORT).split('.');
  if (!SORTABLE.includes(id) || (dir !== 'asc' && dir !== 'desc')) return parseSort(null);
  return [{ id, desc: dir === 'desc' }];
};

const statusVariant: Record<UserStatus, 'success' | 'warning' | 'danger'> = {
  ACTIVE: 'success',
  PENDING: 'warning',
  DEACTIVATED: 'danger',
};

const formatDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '—');

const userGroups = (u: User): { id: string; name: string }[] => u.groups ?? [];

const UsersTableSkeleton = ({ rows = 8 }: { rows?: number }) => (
  <div
    className="bg-surface-elevated rounded-lg border border-outline p-4 space-y-3"
    role="status"
    aria-label="Loading users"
  >
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-4">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-4 w-40 ml-auto" />
      </div>
    ))}
  </div>
);

const GroupChips = ({ groups }: { groups: { id: string; name: string }[] }) => {
  if (groups.length === 0) return <span className="text-ink-subtle text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {groups.map((g) => (
        <Badge key={g.id} variant="neutral">
          {g.name}
        </Badge>
      ))}
    </div>
  );
};

interface UsersTableProps {
  users: User[];
  tenantId: string;
  groups: { id: string; name: string }[];
}

/** Selectable, filterable, paginated users table with bulk group membership. */
const UsersTable = ({ users, tenantId, groups }: UsersTableProps) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [params, setParams] = useSearchParams();
  const sortParam = params.get('sort');
  // Stable identity: a fresh array each render re-sorts, resets the page, and loops.
  const sorting = useMemo(() => parseSort(sortParam), [sortParam]);
  const setSorting: OnChangeFn<SortingState> = (updater) => {
    const [next] = typeof updater === 'function' ? updater(sorting) : updater;
    const value = next ? `${next.id}.${next.desc ? 'desc' : 'asc'}` : DEFAULT_SORT;
    setParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        if (value === DEFAULT_SORT) updated.delete('sort');
        else updated.set('sort', value);
        return updated;
      },
      { replace: true },
    );
  };
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [targetGroup, setTargetGroup] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const bulk = useUsersBulkMembershipLogic(tenantId);
  // Sentinel option to filter to users who belong to no group at all.
  const NO_GROUPS = '__none__';
  const groupOptions = useMemo(
    () => [
      { value: NO_GROUPS, label: 'No groups' },
      ...groups.map((g) => ({ value: g.id, label: g.name })),
    ],
    [groups],
  );

  // Filter by name/email search + group inclusion (member of ANY selected group,
  // or — via the "No groups" sentinel — belonging to none).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const groupSet = new Set(groupFilter);
    const wantsNoGroups = groupSet.has(NO_GROUPS);
    return users.filter((u) => {
      const matchesSearch =
        q === '' || u.email.toLowerCase().includes(q) || (u.name ?? '').toLowerCase().includes(q);
      const ug = userGroups(u);
      const matchesGroup =
        groupSet.size === 0 ||
        (wantsNoGroups && ug.length === 0) ||
        ug.some((g) => groupSet.has(g.id));
      return matchesSearch && matchesGroup;
    });
  }, [users, search, groupFilter]);

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: 'select',
        enableSorting: false,
        header: ({ table }) => (
          <input
            type="checkbox"
            className="accent-primary"
            aria-label="Select all on this page"
            checked={table.getIsAllPageRowsSelected()}
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomePageRowsSelected();
            }}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="accent-primary"
            aria-label={`Select ${row.original.email}`}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        sortingFn: 'text',
        cell: (info) => <span className="text-ink">{info.getValue<string>()}</span>,
      },
      {
        id: 'name',
        accessorFn: (u) => u.name ?? undefined,
        header: 'Name',
        sortingFn: 'text',
        sortUndefined: 'last',
        cell: (info) => (
          <span className="text-ink-muted">{info.getValue<string | undefined>() ?? '—'}</span>
        ),
      },
      {
        id: 'groups',
        header: 'Groups',
        enableSorting: false,
        cell: ({ row }) => <GroupChips groups={userGroups(row.original)} />,
      },
      {
        id: 'scopes',
        header: 'Scopes',
        enableSorting: false,
        cell: ({ row }) => <ScopeSummary scopes={row.original.effectiveScopes ?? []} />,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => (
          <Badge variant={statusVariant[info.getValue<UserStatus>()]}>
            {info.getValue<string>()}
          </Badge>
        ),
      },
      {
        id: 'lastLoginAt',
        // Never signed in sorts last in both directions.
        accessorFn: (u) => u.lastLoginAt ?? undefined,
        header: 'Last sign-in',
        sortingFn: 'text',
        sortDescFirst: true,
        sortUndefined: 'last',
        cell: (info) => (
          <span className="text-ink-muted text-xs">
            {formatDate(info.getValue<string | undefined>() ?? null)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-right">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(`/users/${row.original.id}`)}
            >
              Manage
            </Button>
          </div>
        ),
      },
    ],
    [navigate],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, rowSelection },
    getRowId: (u) => u.id,
    enableRowSelection: true,
    enableSortingRemoval: false,
    sortDescFirst: false,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  const selectedUsers = useMemo(
    () => users.filter((u) => rowSelection[u.id]),
    [users, rowSelection],
  );
  const targetGroupId = targetGroup[0];
  const targetGroupName = groups.find((g) => g.id === targetGroupId)?.name;

  const toAdd = targetGroupId
    ? selectedUsers.filter((u) => !userGroups(u).some((g) => g.id === targetGroupId))
    : [];
  const toRemove = targetGroupId
    ? selectedUsers.filter((u) => userGroups(u).some((g) => g.id === targetGroupId))
    : [];

  const runBulk = async (kind: 'add' | 'remove') => {
    if (!targetGroupId) return;
    const add = kind === 'add' ? toAdd.map((u) => u.id) : [];
    const remove = kind === 'remove' ? toRemove.map((u) => u.id) : [];
    if (add.length === 0 && remove.length === 0) return;
    try {
      await bulk.apply({ groupId: targetGroupId, add, remove });
      setNotice(
        kind === 'add'
          ? `Added ${add.length} ${add.length === 1 ? 'user' : 'users'} to ${targetGroupName}.`
          : `Removed ${remove.length} ${remove.length === 1 ? 'user' : 'users'} from ${targetGroupName}.`,
      );
      setRowSelection({});
    } catch {
      // surfaced via bulk.error
    }
  };

  // "Select all N matching" — client-side pagination keeps every filtered row in
  // memory, so selection can span pages, not just the visible page.
  const selectAllMatching = () => {
    const next: RowSelectionState = {};
    for (const u of filtered) next[u.id] = true;
    setRowSelection(next);
  };

  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();
  const allMatchingSelected = filtered.length > 0 && selectedIds.length >= filtered.length;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Toolbar: search + group-inclusion filter */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          aria-label="Search users"
        />
        <div className="w-56">
          <Select
            options={groupOptions}
            value={groupFilter}
            onChange={setGroupFilter}
            multiple
            placeholder="Filter by group"
            aria-label="Filter by group"
          />
        </div>
        <span className="ml-auto text-xs text-ink-muted">
          {filtered.length} {filtered.length === 1 ? 'user' : 'users'}
        </span>
      </div>

      {notice && (
        <Banner variant="success">
          <Banner.Body>
            <Banner.Title>{notice}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}
      {bulk.error && (
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>{bulk.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {/* Bulk bar — only when rows are selected */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-outline bg-surface-elevated px-4 py-2">
          <span className="text-sm font-medium text-ink">{selectedIds.length} selected</span>
          {!allMatchingSelected && filtered.length > selectedIds.length && (
            <Button size="sm" variant="ghost" onClick={selectAllMatching}>
              Select all {filtered.length}
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-ink-muted">Group</span>
            <div className="w-52">
              <Select
                options={groupOptions}
                value={targetGroup}
                onChange={setTargetGroup}
                placeholder="Select a group…"
                aria-label="Target group"
              />
            </div>
            <Button
              size="sm"
              onClick={() => void runBulk('add')}
              disabled={bulk.isSubmitting || !targetGroupId || toAdd.length === 0}
            >
              Add{toAdd.length > 0 ? ` (${toAdd.length})` : ''}
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => void runBulk('remove')}
              disabled={bulk.isSubmitting || !targetGroupId || toRemove.length === 0}
            >
              Remove{toRemove.length > 0 ? ` (${toRemove.length})` : ''}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRowSelection({})}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Table — wide (9 columns), so it scrolls horizontally on narrow
          viewports instead of crushing the columns. */}
      <div className="bg-surface-elevated overflow-auto rounded-lg border border-outline">
        <Table.Root className="w-full min-w-[72rem] text-sm">
          <Table.Header sticky>
            {table.getHeaderGroups().map((hg) => (
              <Table.Row key={hg.id}>
                {hg.headers.map((header) => {
                  const label = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext());
                  const sorted = header.column.getIsSorted();
                  return (
                    <Table.HeaderCell
                      key={header.id}
                      className="whitespace-nowrap"
                      aria-sort={
                        sorted === 'asc'
                          ? 'ascending'
                          : sorted === 'desc'
                            ? 'descending'
                            : undefined
                      }
                    >
                      {header.column.getCanSort() ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-ink"
                        >
                          {label}
                          {sorted === 'asc' ? (
                            <ChevronUp size={13} aria-hidden />
                          ) : sorted === 'desc' ? (
                            <ChevronDown size={13} aria-hidden />
                          ) : (
                            <ChevronsUpDown size={13} aria-hidden className="opacity-50" />
                          )}
                        </button>
                      ) : (
                        label
                      )}
                    </Table.HeaderCell>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Header>
          <Table.Body>
            {table.getRowModel().rows.map((row) => (
              <Table.Row key={row.id} selected={row.getIsSelected()}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell key={cell.id} className="whitespace-nowrap">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        {filtered.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-ink-muted">
            No users match your filters.
          </p>
        )}
      </div>

      <Pagination
        page={pageIndex}
        pageCount={pageCount}
        onPageChange={(p) => table.setPageIndex(p)}
      />
    </div>
  );
};

export const UsersPage = () => {
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;
  const { isLoading, data, error } = useUsersListLogic(tenantId);
  const groupsList = useGroupsListLogic(tenantId);

  if (meQuery.isLoading || !meQuery.data) {
    return (
      <AppShell>
        <div className="mb-6">
          <Skeleton className="h-7 w-32 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <UsersTableSkeleton rows={8} />
      </AppShell>
    );
  }

  return (
    <AppShell fill>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-ink">Users</h2>
          <p className="text-ink-muted text-sm">
            People in your tenant with access to SC Platform. Accounts are created on first sign-in
            and activate on their first successful sign-in.
          </p>
        </div>
      </div>

      {error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {isLoading && <UsersTableSkeleton rows={8} />}

      {!isLoading && data && (
        <UsersTable
          users={data.users}
          tenantId={meQuery.data.tenant.id}
          groups={groupsList.data ?? []}
        />
      )}
    </AppShell>
  );
};
